import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { siQueryKeyFactory } from "app/pages/sales-intelligence/query/queryKeyFactory";
import type { AccountSettingKey } from "app/pages/sales-intelligence/services/api/account-settings/types";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { assertDefined } from "app/pages/sales-intelligence/utils/errors/assertions";
import { errorOperationMap } from "app/pages/sales-intelligence/utils/errors/context";

import { retryDelayForSearchResults, retrySearchResultsQuery } from "./utils";

/**
 * Options every search-results query shares, whatever the surface (leads, apps).
 *
 * `refetchOnMount` is deliberately absent: it is inert under `staleTime: Infinity` (react-query still gates
 * `true` — as opposed to `"always"` — on staleness), and the current hooks do not set it uniformly, so putting
 * it here would silently change resolved options rather than preserve them.
 */
export const searchResultsQueryDefaults = {
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: keepPreviousData,
    retry: retrySearchResultsQuery,
    retryDelay: retryDelayForSearchResults,
} as const;

/**
 * Guards the backend-cancelled 204→null response. Paired with `retrySearchResultsQuery`, which retries exactly
 * the `SiError` thrown here — keep the two together so they cannot drift apart.
 */
export function assertSearchResults<T>(response: T | undefined): asserts response is T {
    assertDefined(response, {
        name: "fetchSearchResultsResponse",
        operation: errorOperationMap.emptySearchResults,
    });
}

/**
 * `staleTime: Infinity` — an account setting only changes through `useSetAccountSettingMutation`, which writes
 * the new value through the cache itself, so nothing else can make this entry stale within a session.
 */
export const accountSettingQueryOptions = (key: AccountSettingKey) =>
    queryOptions({
        queryKey: siQueryKeyFactory.accountSetting(key),
        queryFn({ signal }): Promise<string | null> {
            return salesApiService.accountSettings.fetchSetting(key, { cancellation: signal });
        },
        staleTime: Number.POSITIVE_INFINITY,
    });
